import { useState } from 'react';
import { Card, CardContent, CardHeader } from '../../../components/Card';
import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import toast from 'react-hot-toast';
import { Input } from '@/components/Input';

const SPACES: Array<{
  id: string; name: string; slug: string; icon: string; color: string;
  pages: number; visibility: string; desc: string;
}> = [];

const PAGES: Array<{
  id: string; title: string; space: string; path: string; status: string;
  views: number; likes: number; comments: number; author: string; updatedAt: string;
  tags: string[]; excerpt: string;
}> = [];

const ARTICLE_MD = `# API Authentication Guide

This guide covers authentication methods for the TeamOne API.

## Overview

TeamOne supports the following authentication methods:

- **API Keys** - For server-to-server communication
- **OAuth 2.0** - For user-authorized access  
- **JWT Tokens** - For session-based authentication

## API Keys

\`\`\`bash
curl -H "Authorization: Bearer YOUR_API_KEY" \\
     https://api.teamone.com/v1/users
\`\`\`

> **Note:** Keep your API keys secure and never commit them to version control.

## OAuth 2.0

OAuth 2.0 is the recommended auth flow for user-facing applications.

### Flow Steps

1. Redirect user to \`/oauth/authorize\`
2. User grants permission
3. Exchange authorization code for access token
4. Use access token for API calls

## JWT Tokens

JWTs are used for session-based authentication within the TeamOne platform. Tokens expire after 24 hours by default.

## Related

- Rate Limiting Guide
- Error Handling Reference
- API Changelog`;

const statusBadgeVariant: Record<string, 'success' | 'warning' | 'default'> = {
  published: 'success', draft: 'warning', archived: 'default',
};
const visVariant: Record<string, string> = {
  public: 'text-success', internal: 'text-primary', restricted: 'text-warning',
};

export function WikiArticles() {
  const [activeView, setActiveView] = useState<'spaces' | 'pages' | 'search'>('spaces');
  const [selectedPage, setSelectedPage] = useState<typeof PAGES[0] | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [editContent, setEditContent] = useState(ARTICLE_MD);
  const [editorMode, setEditorMode] = useState<'write' | 'preview'>('write');
  const [selectedSpace, setSelectedSpace] = useState<typeof SPACES[0] | null>(null);

  const filteredPages = PAGES.filter(p => {
    const matchStatus = statusFilter === 'All' || p.status === statusFilter;
    const matchSearch = !searchQuery || p.title.toLowerCase().includes(searchQuery.toLowerCase()) || p.tags.some(t => t.includes(searchQuery.toLowerCase())) || p.space.toLowerCase().includes(searchQuery.toLowerCase());
    const matchSpace = !selectedSpace || p.space === selectedSpace.name;
    return matchStatus && matchSearch && matchSpace;
  });

  if (isEditing && selectedPage) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Button variant="unstyled" size="unstyled" onClick={() => { setIsEditing(false); }} className="hover:text-primary transition-colors font-semibold">← Cancel</Button>
          <span>/</span>
          <span className="text-muted-foreground">{selectedPage.space}</span>
          <span>/</span>
          <span className="text-foreground font-semibold">{selectedPage.title}</span>
        </div>
        <Card>
          <CardContent className="p-6 space-y-4">
            <Input defaultValue={selectedPage.title} className="w-full text-2xl font-semibold bg-transparent border-b-2 border-primary/30 focus:border-primary focus:outline-none text-foreground pb-2" placeholder="Page title..." />
            <div className="flex gap-2 border-b border-border">
              {(['write', 'preview'] as const).map(m => (
                <Button variant="unstyled" size="unstyled" key={m} onClick={() => setEditorMode(m)}
                  className={`pb-3 px-4 text-sm font-semibold capitalize transition-all relative ${editorMode === m ? 'text-primary' : 'text-muted-foreground'}`}>
                  {m}
                  {editorMode === m && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />}
                </Button>
              ))}
              <Button variant="unstyled" size="unstyled" className="pb-3 px-4 text-sm font-semibold text-muted-foreground hover:text-foreground ml-auto">History</Button>
            </div>
            {editorMode === 'write' ? (
              <textarea value={editContent} onChange={e => setEditContent(e.target.value)} rows={20}
                className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 resize-y" />
            ) : (
              <div className="min-h-96 p-4 bg-background rounded-lg border border-border prose prose-invert max-w-none text-foreground text-sm">
                {editContent.split('\n').map((line, i) => {
                  if (line.startsWith('# ')) return <h1 key={i} className="text-foreground mt-4 font-display text-page-title">{line.slice(2)}</h1>;
                  if (line.startsWith('## ')) return <h2 key={i} className="text-foreground mt-3 font-display text-section-title">{line.slice(3)}</h2>;
                  if (line.startsWith('- **')) return <li key={i} className="text-foreground ml-4">{line.slice(2)}</li>;
                  if (line.startsWith('> **')) return <blockquote key={i} className="border-l-4 border-warning pl-4 text-warning text-xs my-2">{line.slice(2)}</blockquote>;
                  if (line.startsWith('```')) return null;
                  if (line.trim()) return <p key={i} className="text-foreground">{line}</p>;
                  return <br key={i} />;
                })}
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1.5">Tags</label>
                <Input defaultValue={selectedPage.tags.join(', ')} className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50" />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1.5">Category / Space</label>
                <select className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none">
                  {SPACES.map(s => <option key={s.id}>{s.name}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="secondary" onClick={() => { toast.success('Saved as draft!'); setIsEditing(false); }}>Save Draft</Button>
              <Button variant="primary" className="flex-1" onClick={() => { toast.success('Page published!'); setIsEditing(false); }}>Publish</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (selectedPage && !isEditing) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Button variant="unstyled" size="unstyled" onClick={() => setSelectedPage(null)} className="hover:text-primary transition-colors font-semibold">Wiki</Button>
          <span>/</span>
          <Button variant="unstyled" size="unstyled" onClick={() => { setSelectedSpace(SPACES.find(s => s.name === selectedPage.space) || null); setSelectedPage(null); setActiveView('pages'); }} className="hover:text-primary transition-colors">{selectedPage.space}</Button>
          <span>/</span>
          <span className="text-foreground font-semibold">{selectedPage.title}</span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3 space-y-4">
            <Card>
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <Badge variant={statusBadgeVariant[selectedPage.status]}>{selectedPage.status}</Badge>
                  <span className="text-xs text-muted-foreground">{selectedPage.space}</span>
                  {selectedPage.tags.map(t => <span key={t} className="text-xs px-1.5 py-0.5 bg-primary/10 text-primary rounded-md">{t}</span>)}
                </div>
                <h1 className="tracking-tight text-foreground mb-2 font-display text-page-title">{selectedPage.title}</h1>
                <p className="text-xs text-muted-foreground mb-6">By {selectedPage.author} · Updated {selectedPage.updatedAt} · {selectedPage.path}</p>
                <div className="space-y-3 text-sm text-foreground leading-relaxed">
                  {editContent.split('\n').map((line, i) => {
                    if (line.startsWith('# ')) return <h1 key={i} className="text-foreground mt-6 first:mt-0 font-display text-page-title">{line.slice(2)}</h1>;
                    if (line.startsWith('## ')) return <h2 key={i} className="text-foreground mt-4 border-b border-border pb-1 font-display text-section-title">{line.slice(3)}</h2>;
                    if (line.startsWith('- **')) return <li key={i} className="text-foreground ml-4 list-disc" dangerouslySetInnerHTML={{ __html: line.slice(2).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />;
                    if (line.startsWith('> **')) return <blockquote key={i} className="border-l-4 border-warning pl-4 text-warning text-sm my-2">{line.slice(4).replace(/\*\*/g, '')}</blockquote>;
                    if (line.startsWith('```')) return <div key={i} className="bg-background border border-border rounded-lg px-4 py-3 font-mono text-xs text-success my-2">bash</div>;
                    if (line.startsWith('curl')) return <code key={i} className="block font-mono text-xs text-muted-foreground px-4">$ {line}</code>;
                    if (line.trim()) return <p key={i}>{line}</p>;
                    return null;
                  })}
                </div>
                <div className="mt-8 pt-6 border-t border-border flex items-center gap-6">
                  <Button variant="unstyled" size="unstyled" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-success transition-colors font-semibold" onClick={() => toast.success('Liked!')}>
                    {selectedPage.likes} likes
                  </Button>
                  <Button variant="unstyled" size="unstyled" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors font-semibold" onClick={() => toast.success('Watching page...')}>
                    Watch
                  </Button>
                  <Button variant="unstyled" size="unstyled" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-info transition-colors font-semibold" onClick={() => toast.success('Link copied!')}>
                    Share
                  </Button>
                  <span className="text-xs text-muted-foreground ml-auto">{selectedPage.comments} comments · {selectedPage.views} views</span>
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="space-y-4">
            <Card>
              <CardContent className="p-4 space-y-3">
                <Button variant="primary" size="sm" className="w-full" onClick={() => setIsEditing(true)}>Edit Page</Button>
                <Button variant="secondary" size="sm" className="w-full" onClick={() => toast.success('Version history...')}>History</Button>
                <Button variant="secondary" size="sm" className="w-full" onClick={() => toast.success('Page settings...')}>Settings</Button>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="bg-card/30 px-4 py-3"><p className="text-xs font-semibold text-muted-foreground">Page Info</p></CardHeader>
              <CardContent className="p-4 space-y-2">
                {[
                  { label: 'Space', value: selectedPage.space },
                  { label: 'Author', value: selectedPage.author },
                  { label: 'Updated', value: selectedPage.updatedAt },
                  { label: 'Views', value: selectedPage.views.toString() },
                ].map((row, i) => (
                  <div key={i} className="flex justify-between text-xs">
                    <span className="text-muted-foreground">{row.label}</span>
                    <span className="text-foreground font-semibold">{row.value}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="tracking-tight text-foreground font-display text-page-title">Corporate <span className="text-foreground">Wiki</span></h1>
          <p className="text-muted-foreground mt-1">Spaces · Pages · Templates · Version History</p>
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={() => setActiveView('search')}>Search</Button>
          <Button variant="primary" onClick={() => { setSelectedPage(PAGES[0]); setIsEditing(true); }}>+ New Page</Button>
        </div>
      </div>

      {/* Nav tabs */}
      <div className="flex gap-2 border-b border-border">
        {[
          { id: 'spaces', label: 'Spaces' },
          { id: 'pages', label: 'All Pages' },
          { id: 'search', label: 'Search' },
        ].map(t => (
          <Button variant="unstyled" size="unstyled" key={t.id} onClick={() => setActiveView(t.id as any)}
            className={`pb-4 px-4 text-sm font-semibold transition-all relative ${activeView === t.id ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
            {t.label}
            {activeView === t.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />}
          </Button>
        ))}
        {selectedSpace && (
          <Button variant="unstyled" size="unstyled" onClick={() => setSelectedSpace(null)} className="ml-auto text-xs text-muted-foreground hover:text-foreground pb-4 px-4 flex items-center gap-1">
            Clear space filter
          </Button>
        )}
      </div>

      {activeView === 'spaces' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {SPACES.map(space => (
            <Card key={space.id} className="hover:-translate-y-1 transition-all duration-300 cursor-pointer group" onClick={() => { setSelectedSpace(space); setActiveView('pages'); }}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className={`w-12 h-12 rounded-lg ${space.color} flex items-center justify-center text-2xl shrink-0 group-hover:scale-110 transition-transform`}>
                    {space.icon}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-foreground group-hover:text-primary transition-colors">{space.name}</p>
                    <span className={`text-xs font-semibold ${visVariant[space.visibility]}`}>● {space.visibility}</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mb-3 leading-relaxed">{space.desc}</p>
                <div className="flex justify-between items-center text-xs text-muted-foreground">
                  <span>{space.pages} pages</span>
                  <span className="text-primary font-semibold group-hover:underline">Browse →</span>
                </div>
              </CardContent>
            </Card>
          ))}
          <Card className="border-dashed hover:-translate-y-1 transition-all cursor-pointer" onClick={() => toast.success('Create new space...')}>
            <CardContent className="p-6 flex flex-col items-center justify-center gap-3 min-h-[160px]">
              <div className="w-12 h-12 rounded-lg border-2 border-dashed border-border flex items-center justify-center text-2xl text-muted-foreground">+</div>
              <p className="font-semibold text-muted-foreground">New Space</p>
            </CardContent>
          </Card>
        </div>
      )}

      {activeView === 'pages' && (
        <div className="space-y-4">
          <div className="flex gap-3 flex-wrap">
            {selectedSpace && (
              <div className="flex items-center gap-2 px-3 py-2 bg-primary/10 border border-primary/30 rounded-lg">
                <span className="text-sm">{selectedSpace.icon}</span>
                <span className="text-sm font-semibold text-primary">{selectedSpace.name}</span>
              </div>
            )}
            <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search pages..."
              className="px-4 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 flex-1 max-w-xs" />
            {['All', 'published', 'draft', 'archived'].map(s => (
              <Button variant="unstyled" size="unstyled" key={s} onClick={() => setStatusFilter(s)}
                className={`px-3 py-2 text-xs font-semibold rounded-lg border transition-all capitalize ${statusFilter === s ? 'bg-primary text-white border-primary' : 'border-border text-muted-foreground hover:border-primary/50'}`}>
                {s}
              </Button>
            ))}
          </div>
          <div className="space-y-3">
            {filteredPages.map(page => (
              <Card key={page.id} className="hover:-translate-y-0.5 transition-all cursor-pointer group" onClick={() => setSelectedPage(page)}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <Badge variant={statusBadgeVariant[page.status]}>{page.status}</Badge>
                        <span className="text-xs font-semibold text-muted-foreground">{page.space}</span>
                        <span className="text-xs text-muted-foreground font-mono">{page.path}</span>
                      </div>
                      <h3 className="text-foreground group-hover:text-primary transition-colors font-display text-card-title">{page.title}</h3>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{page.excerpt}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span>By {page.author}</span>
                        <span>· {page.updatedAt}</span>
                        <span>· {page.views}</span>
                        <span>· {page.likes}</span>
                        {page.tags.map(t => <span key={t} className="px-1.5 py-0.5 bg-primary/10 text-primary rounded-md font-semibold">{t}</span>)}
                      </div>
                    </div>
                    <Button variant="secondary" size="sm" onClick={(e: React.MouseEvent) => { e.stopPropagation(); setSelectedPage(page); setIsEditing(true); }}>Edit</Button>

                  </div>
                </CardContent>
              </Card>
            ))}
            {filteredPages.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <p className="text-4xl mb-2"></p>
                <p className="font-semibold">No pages found</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeView === 'search' && (
        <div className="space-y-4 max-w-2xl">
          <Input autoFocus value={searchQuery} onChange={e => { setSearchQuery(e.target.value); }} placeholder="Search all wiki pages, spaces, tags..."
            className="w-full px-5 py-4 bg-background border border-border rounded-lg text-foreground text-lg focus:outline-none focus:ring-2 focus:ring-primary/50" />
          {searchQuery && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground font-semibold">{filteredPages.length} results for "{searchQuery}"</p>
              {filteredPages.map(page => (
                <Card key={page.id} className="cursor-pointer hover:bg-card/30 transition-colors" onClick={() => { setSelectedPage(page); setActiveView('pages'); }}>
                  <CardContent className="p-4">
                    <p className="font-semibold text-foreground hover:text-primary text-sm">{page.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{page.space} · {page.path}</p>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{page.excerpt}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
